import { CmsSectionShell } from './CmsSectionShell.tsx';
import { CmsSectionState } from './CmsSectionState.tsx';
import { SectionHeading } from '../SectionHeading.tsx';
import { themeOf, type ThemeClasses } from './cmsTheme.ts';
import { useFestivalData } from '../../lib/festivalData.ts';
import { cdnVariant, cdnSrcSet } from '../../lib/cdn.ts';
import type { PeopleSection, Person } from '../../lib/types.ts';

/**
 * Lists people from program.json — either the board (bestyrelse) or the organizer group
 * (arrangørgruppen), chosen via `group`. Uniform cards in the section card shade with a square
 * photo, name and title; optional contact (email/phone). No per-person accent colors.
 *
 * Two columns on phones, four from md up — the v2 grid for a wall of faces.
 */
export function CmsPeopleSection({ theme, guitar, group, heading, showContact }: PeopleSection) {
  const t = themeOf(theme);
  const { data, loading, error } = useFestivalData();
  const source = !data
    ? []
    : group === 'board'
      ? data.program.boardMembers
      : group === 'secretariat'
        ? (data.program.secretariatMembers ?? [])
        : data.program.organizerGroupMembers;
  const people = [...source].sort((a, b) => a.displayOrder - b.displayOrder);

  return (
    <CmsSectionShell theme={theme} guitar={guitar}>
      {heading && <SectionHeading color={t.heading} eyebrowColor={t.eyebrow}>{heading}</SectionHeading>}

      <CmsSectionState
        loading={loading}
        error={!!error}
        loadingLabel="Indlæser…"
        bodyClass={t.body}
        errorMessage="Listen kunne ikke indlæses."
      >
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
          {people.map(person => (
            <PersonCard key={person.id} person={person} showContact={showContact} t={t} />
          ))}
        </div>
      </CmsSectionState>
    </CmsSectionShell>
  );
}

function PersonCard({ person, showContact, t }: { person: Person; showContact?: boolean; t: ThemeClasses }) {
  return (
    <div className={`rounded-card overflow-hidden ${t.card.bg} ${t.card.text}`}>
      <div className={`aspect-square w-full ${t.img}`}>
        {person.images?.desktop ? (
          <img
            src={cdnVariant(person.images.desktop, 'mobile')}
            srcSet={cdnSrcSet(person.images.desktop) || undefined}
            sizes="(min-width: 768px) 280px, 50vw"
            alt={person.name}
            className="w-full h-full object-cover"
            style={{ objectPosition: '50% 20%' }}
            loading="lazy"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <span className={`font-display text-3xl ${t.card.muted}`}>{person.name.charAt(0)}</span>
          </div>
        )}
      </div>
      <div className="p-4 md:p-5">
        <div className="font-display text-lg md:text-xl leading-tight">{person.name}</div>
        {person.title && <div className={`text-sm mt-1 ${t.card.muted}`}>{person.title}</div>}
        {showContact && (person.email || person.phone) && (
          <div className="mt-3 space-y-0.5 text-sm">
            {person.email && (
              <a href={`mailto:${person.email}`} className="block hover:text-mf-coral transition-colors wrap-break-word">
                {person.email}
              </a>
            )}
            {person.phone && (
              <a href={`tel:${person.phone.replace(/\s+/g, '')}`} className={`block hover:text-mf-coral transition-colors ${t.card.muted}`}>
                {person.phone}
              </a>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
